import { useState, useEffect } from 'react';
import {
  RefreshCw,
  Loader2,
  Phone,
  Users,
  Megaphone,
  MessageSquare,
  GitBranch,
  FileText,
  Wrench,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@easyflow/ui-frontend/components/ui/dialog';
import { Card, CardContent } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@easyflow/ui-frontend/components/ui/accordion';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import WorkflowBuilderAIConfigCard from '@/components/ai/WorkflowBuilderAIConfigCard';
import { getErrorMessage } from '../../lib/utils';


interface TenantContext {
  trunks: { id: string; name: string; provider: string; is_active: boolean }[];
  extensions: { id: string; extension: string; display_name: string; is_active: boolean }[];
  campaigns: { id: string; name: string; type: string; status: string }[];
  senders: { id: string; phone_number: string; channel: string; friendly_name: string; is_active: boolean }[];
  pipelines: { id: string; name: string; stages: { id: string; name: string }[] }[];
  forms: { id: string; name: string; fields: string[] }[];
  mcpTools: { id: string; name: string; description: string; server_name: string }[];
  workflowNodes: { id: string; displayName: string; type: string; category: string; description: string }[];
  syncedAt: string;
}

interface WorkflowBuilderAgentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SYSTEM_PROMPT = `Sei un assistente AI specializzato nella creazione di workflow per il sistema EDG VoIP.

## Filosofia di lavoro:
- Sii ANALITICO e PROFESSIONALE: fai tutte le domande necessarie per capire l'esigenza
- SEMPLIFICA SEMPRE: proponi la soluzione più semplice ed efficace possibile
- Evita workflow complessi a meno che non sia la REALE ESIGENZA dell'utente

## Regole di semplificazione:
1. Meno nodi = meglio (se raggiunge l'obiettivo)
2. Evita condizioni multiple se una sola basta
3. Proponi prima la versione "minima funzionante"
4. Solo se l'utente conferma necessità, aggiungi complessità

## Flusso conversazionale:
1. Chiedi l'obiettivo in modo chiaro e diretto
2. Proponi la soluzione PIÙ SEMPLICE possibile
3. Chiedi: "Questo copre le tue esigenze o serve qualcosa in più?"
4. Se serve di più, aggiungi incrementalmente
5. Conferma e genera

## Comportamento:
- NON creare workflow da 10+ nodi se ne bastano 3
- Se l'utente è vago, proponi esempio semplice e chiedi feedback
- Usa SOLO le risorse effettivamente disponibili nel contesto
- Se una risorsa non esiste, suggerisci di crearla prima`;

export default function WorkflowBuilderAgentModal({ open, onOpenChange }: WorkflowBuilderAgentModalProps) {
  const { toast } = useToast();
  const [context, setContext] = useState<TenantContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadContext = async () => {
    setLoading(true);
    try {
      console.log('[WorkflowBuilderAgent] Loading context...');
      const res = await apiClient.get<TenantContext>('/ai/workflow-builder/context');
      console.log('[WorkflowBuilderAgent] Context response:', res);
      if (res.success && res.data) {
        console.log('[WorkflowBuilderAgent] Setting context with data:', res.data);
        setContext(res.data);
      } else {
        console.log('[WorkflowBuilderAgent] No context data in response');
      }
    } catch (error) {
      console.error('[WorkflowBuilderAgent] Error loading context:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadContext();
    }
  }, [open]);

  const syncResources = async () => {
    setSyncing(true);
    try {
      console.log('[WorkflowBuilderAgent] Syncing resources...');
      const res = await apiClient.post<{ syncedAt: string; stats: { trunks: number; extensions: number; campaigns: number; senders: number; pipelines: number; forms: number; mcpTools: number; workflowNodes: number } }>('/ai/workflow-builder/sync');
      console.log('[WorkflowBuilderAgent] Sync response:', res);
      if (res.success && res.data) {
        console.log('[WorkflowBuilderAgent] Sync successful, stats:', res.data.stats);
        toast({
          title: 'Risorse sincronizzate',
          description: `Trunk: ${res.data.stats.trunks}, Estensioni: ${res.data.stats.extensions}, Campagne: ${res.data.stats.campaigns}`,
        });
        await loadContext();
      } else {
        console.log('[WorkflowBuilderAgent] Sync failed - invalid response');
        toast({
          title: 'Errore sincronizzazione',
          description: 'Risposta non valida dal server',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('[WorkflowBuilderAgent] Sync error:', error);
      toast({
        title: 'Errore sincronizzazione',
        description: getErrorMessage(error) || 'Errore durante la sincronizzazione',
        variant: 'destructive'
      });
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-purple-500" />
            Workflow Builder AI Agent
          </DialogTitle>
          <DialogDescription>
            Configura l'agente AI che aiuta a creare workflow automaticamente
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="model" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="model">Modello AI</TabsTrigger>
            <TabsTrigger value="config">Configurazione</TabsTrigger>
            <TabsTrigger value="context">Contesto RAG</TabsTrigger>
          </TabsList>

          <TabsContent value="model" className="flex-1 overflow-auto space-y-4 mt-4">
            <WorkflowBuilderAIConfigCard />
          </TabsContent>

          <TabsContent value="config" className="flex-1 overflow-auto space-y-4 mt-4">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium">System Prompt</h4>
                  <Badge variant="secondary">Read-only</Badge>
                </div>
                <Textarea 
                  value={SYSTEM_PROMPT} 
                  readOnly 
                  className="min-h-[200px] font-mono text-xs bg-gray-100 dark:bg-gray-800"
                />
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <h4 className="font-medium">Sincronizza Risorse Tenant</h4>
                    <p className="text-sm text-gray-500">
                      Aggiorna il contesto con trunk, estensioni, campagne, sender e altri dati
                    </p>
                    {context?.syncedAt && (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <Clock className="h-3 w-3" />
                        Ultima sync: {formatDate(context.syncedAt)}
                      </div>
                    )}
                  </div>
                  <Button onClick={syncResources} disabled={syncing}>
                    {syncing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Sincronizza
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="context" className="flex-1 overflow-auto mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : !context ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <GitBranch className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <h4 className="font-medium mb-2">Nessun contesto sincronizzato</h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Clicca "Sincronizza" per caricare le risorse del tenant
                  </p>
                  <Button onClick={syncResources} disabled={syncing}>
                    {syncing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Sincronizza Risorse
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Accordion type="multiple" className="space-y-2">
                <AccordionItem value="trunks" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-blue-500" />
                      <span>Trunk SIP ({context.trunks.length})</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {context.trunks.map(t => (
                        <div key={t.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded">
                          <div>
                            <span className="font-medium">{t.name}</span>
                            <span className="text-sm text-gray-500 ml-2">({t.provider})</span>
                          </div>
                          {t.is_active ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <Badge variant="secondary">Inattivo</Badge>
                          )}
                        </div>
                      ))}
                      {context.trunks.length === 0 && (
                        <p className="text-sm text-gray-500 py-2">Nessun trunk configurato</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="extensions" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-green-500" />
                      <span>Estensioni ({context.extensions.length})</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 gap-2">
                      {context.extensions.slice(0, 20).map(e => (
                        <div key={e.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded">
                          <div>
                            <span className="font-mono">{e.extension}</span>
                            <span className="text-sm text-gray-500 ml-2">{e.display_name}</span>
                          </div>
                        </div>
                      ))}
                      {context.extensions.length > 20 && (
                        <p className="text-sm text-gray-500 col-span-2">+{context.extensions.length - 20} altre estensioni</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="campaigns" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Megaphone className="h-4 w-4 text-orange-500" />
                      <span>Campagne ({context.campaigns.length})</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {context.campaigns.map(c => (
                        <div key={c.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded">
                          <div>
                            <span className="font-medium">{c.name}</span>
                            <Badge variant="outline" className="ml-2">{c.type}</Badge>
                          </div>
                          <Badge variant={c.status === 'active' ? 'default' : 'secondary'}>{c.status}</Badge>
                        </div>
                      ))}
                      {context.campaigns.length === 0 && (
                        <p className="text-sm text-gray-500 py-2">Nessuna campagna</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="senders" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-purple-500" />
                      <span>Numeri Mittenti SMS/WhatsApp ({context.senders.length})</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {context.senders.map(s => (
                        <div key={s.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded">
                          <div>
                            <span className="font-mono">{s.phone_number}</span>
                            <Badge variant="outline" className="ml-2">{s.channel.toUpperCase()}</Badge>
                          </div>
                          <span className="text-sm text-gray-500">{s.friendly_name}</span>
                        </div>
                      ))}
                      {context.senders.length === 0 && (
                        <p className="text-sm text-gray-500 py-2">Nessun sender configurato</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="pipelines" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <GitBranch className="h-4 w-4 text-cyan-500" />
                      <span>Pipeline CRM ({context.pipelines.length})</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3">
                      {context.pipelines.map(p => (
                        <div key={p.id} className="py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded">
                          <div className="font-medium mb-2">{p.name}</div>
                          <div className="flex flex-wrap gap-1">
                            {p.stages.map((s, i) => (
                              <span key={s.id}>
                                <Badge variant="outline" className="text-xs">{s.name}</Badge>
                                {i < p.stages.length - 1 && <span className="mx-1 text-gray-400">→</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                      {context.pipelines.length === 0 && (
                        <p className="text-sm text-gray-500 py-2">Nessuna pipeline</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="forms" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-yellow-500" />
                      <span>Form Embed ({context.forms.length})</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {context.forms.map(f => (
                        <div key={f.id} className="py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded">
                          <div className="font-medium">{f.name}</div>
                          <div className="text-xs text-gray-500">Campi: {f.fields.join(', ')}</div>
                        </div>
                      ))}
                      {context.forms.length === 0 && (
                        <p className="text-sm text-gray-500 py-2">Nessun form</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="mcp" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-red-500" />
                      <span>MCP Tools ({context.mcpTools.length})</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2">
                      {context.mcpTools.map(m => (
                        <div key={m.id} className="py-2 px-3 bg-gray-50 dark:bg-gray-800 rounded">
                          <div className="font-medium">{m.name}</div>
                          <div className="text-xs text-gray-500">{m.description}</div>
                          <Badge variant="outline" className="mt-1 text-xs">{m.server_name}</Badge>
                        </div>
                      ))}
                      {context.mcpTools.length === 0 && (
                        <p className="text-sm text-gray-500 py-2">Nessun tool MCP</p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="nodes" className="border rounded-lg px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-2">
                      <GitBranch className="h-4 w-4 text-indigo-500" />
                      <span>Nodi Workflow ({context.workflowNodes.length})</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="max-h-64 overflow-auto">
                      {['Triggers', 'Flow Control', 'Utilities'].map(cat => {
                        const nodes = context.workflowNodes.filter(n => n.category === cat);
                        if (nodes.length === 0) return null;
                        return (
                          <div key={cat} className="mb-3">
                            <h5 className="font-medium text-sm mb-1">{cat}</h5>
                            <div className="grid grid-cols-2 gap-1">
                              {nodes.map(n => (
                                <div key={n.id} className="text-xs py-1 px-2 bg-gray-50 dark:bg-gray-800 rounded">
                                  <code className="text-purple-500">{n.id}</code>
                                  <span className="text-gray-500 ml-1">- {n.displayName}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
